import { Card, H4, SegmentedControl } from "@blueprintjs/core";
import { segmentTrackChangeDropoffChartXAxis } from "in_queue/analytics";
import { useAllQueueProjects } from "in_queue/contexts/ProjectDataContext";
import { TrackedProject } from "in_queue/types/projectType";
import { ScenarioMetricsMapping } from "in_queue/types/scenarioMetricsType";
import { ScenarioMetadata } from "in_queue/types/scenarioType";
import { isProjectExcluded } from "in_queue/types/scenarioUtils";
import { findLatestBaseScenario } from "in_queue/utils/baseScenario";
import {
    buildClusterMwMap,
    computeScenarioDropoff,
    formatOwnDownsize
} from "in_queue/utils/dropoffStats";
import { formatPercent } from "in_queue/utils/formatting";
import { getCostAssumptionText } from "in_queue/utils/scenarios";
import { useState } from "react";
import {
    CartesianGrid,
    ResponsiveContainer,
    Scatter,
    ScatterChart,
    Tooltip,
    XAxis,
    YAxis
} from "recharts";
import {
    countAxisScale,
    percentAxisScale,
    rankPercentileAxisScale
} from "./axisScale";
import { ChartPlaceholder, getScenarioTitle } from "./chartHelpers";
import {
    ChartMetric,
    chartMetricValue,
    CHART_METRIC_AXIS_LABELS,
    formatChartMetric
} from "./chartMetric";
import css from "./charts.module.scss";

type DropoffXAxis = "SCENARIO_COUNT" | "MW_PERCENT";

interface Props {
    completed: ScenarioMetadata[];
    allScenarios: ScenarioMetadata[];
    project: TrackedProject;
    scenarioMetrics: ScenarioMetricsMapping;
    metric: ChartMetric;
}

// Marker types for the scatter shapes. Kept in module scope so the
// inline `renderShape` function can dispatch on payload.marker.
type Marker = "base" | "self" | "sensitivity";

const MARKER_COLOR: Record<Marker, string> = {
    base: "#f7a23b",
    self: "#7c5cff",
    sensitivity: "#2fb0ff"
};

interface Row {
    scenarioId: string;
    label: string;
    x: number;
    costValue: number;
    rank?: number;
    totalProjects?: number;
    rankPercentile?: number;
    otherProjectCount: number;
    otherDropoffMw: number;
    otherDropoffPct: number;
    ownDownsizePct: number;
    ownNrisDownsizePct: number;
    costAssumption: string;
    marker: Marker;
}

/**
 * Cost-by-dropoff scatter plot. X axis = how much of the cluster is
 * dropped (# of other projects or % of cluster MW); Y axis = either
 * cost ($) or rank-percentile, toggleable. The base scenario shows as a
 * star marker, "this project downsized too" shows as an X, and the rest
 * are circles.
 */
export const DropoffChart: React.FC<Props> = ({
    completed,
    allScenarios,
    project,
    scenarioMetrics,
    metric
}) => {
    const [dropoffX, setDropoffX] = useState<DropoffXAxis>("MW_PERCENT");
    const allQueueProjectsL = useAllQueueProjects();

    const valueOf = (sid: string): number | undefined =>
        chartMetricValue(scenarioMetrics[sid], metric);
    const yFormatter = (v: number) => formatChartMetric(metric, v);

    if (allQueueProjectsL === "loading") {
        return (
            <ChartPlaceholder
                title="Cost by dropoff"
                message="Loading cluster project data…"
            />
        );
    }

    // Matches the SensitivityChart logic so the two charts agree on which
    // scenario is the "base case".
    const base = findLatestBaseScenario(
        allScenarios,
        (s) => valueOf(s.scenarioId) !== undefined
    );
    const baseScenarioId = base?.scenarioId;

    const mwMap = buildClusterMwMap(allQueueProjectsL);

    const rows: Row[] = [];
    for (const s of completed) {
        const metrics = scenarioMetrics[s.scenarioId];
        const costValue = valueOf(s.scenarioId);
        if (costValue === undefined) continue;

        const dropoff = computeScenarioDropoff(s, project.projectId, mwMap);

        // Skip scenarios where the current project is fully dropped
        // (ERIS *and* NRIS both reduced 100%). With the project gone
        // there's no meaningful $/MW or rank to plot. Asymmetric
        // reductions (e.g. eris=100 only) are NOT drops — the project
        // still participates in ERIS-thermal cost allocation — so we use
        // the canonical `isProjectExcluded` predicate rather than the
        // ERIS-only `ownDownsizePct`.
        if (isProjectExcluded(s.assumptions[project.projectId])) continue;

        const x =
            dropoffX === "SCENARIO_COUNT"
                ? dropoff.otherProjectCount
                : dropoff.otherDropoffPct;

        let marker: Marker = "sensitivity";
        if (s.scenarioId === baseScenarioId) marker = "base";
        // "self" = the current project itself was modified in this
        // scenario. Check BOTH services: an NRIS-only downsize
        // (eris=0, nris>0) still means the user changed this project.
        else if (dropoff.ownDownsizePct > 0 || dropoff.ownNrisDownsizePct > 0) {
            marker = "self";
        }

        rows.push({
            scenarioId: s.scenarioId,
            label: getScenarioTitle(s),
            x,
            costValue,
            rank: metrics?.rank,
            totalProjects: metrics?.totalRanked,
            rankPercentile: metrics?.rankPercentile,
            otherProjectCount: dropoff.otherProjectCount,
            otherDropoffMw: dropoff.otherDropoffMw,
            otherDropoffPct: dropoff.otherDropoffPct,
            ownDownsizePct: dropoff.ownDownsizePct,
            ownNrisDownsizePct: dropoff.ownNrisDownsizePct,
            costAssumption: getCostAssumptionText(s),
            marker
        });
    }

    // The outer ProjectCharts gate ensures completed.length >= 2; this is
    // a defensive no-op in case rank/dropoff data lags behind.
    if (rows.length < 2) {
        return null;
    }

    const rankPercentiles = rows
        .map((r) => r.rankPercentile)
        .filter((p): p is number => p !== undefined);
    const xAxisScale =
        dropoffX === "SCENARIO_COUNT"
            ? countAxisScale(Math.max(...rows.map((r) => r.otherProjectCount)))
            : percentAxisScale(Math.max(...rows.map((r) => r.otherDropoffPct)));
    const rankAxisScale = rankPercentileAxisScale(rankPercentiles);
    const xTickFormatter =
        dropoffX === "SCENARIO_COUNT"
            ? (v: number) => `${v}`
            : (v: number) => formatPercent(v);
    const xLabel =
        dropoffX === "SCENARIO_COUNT"
            ? "Dropoff: # of other cluster projects with non-zero reduction"
            : "Dropoff: cluster MW removed by sensitivities (% of total)";

    const yLabel = CHART_METRIC_AXIS_LABELS[metric];

    // Single Scatter dataset; shape and color are dispatched per-point
    // via a custom shape renderer. This avoids the Recharts multi-Scatter
    // tooltip cross-talk where hovering one series shows another
    // series' payload (which caused "Own downsize: —" tooltips on
    // self-downsize dots).
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const renderShape = (props: any) => {
        const { cx, cy, payload } = props as {
            cx: number;
            cy: number;
            payload: Row;
        };
        const color = MARKER_COLOR[payload.marker];
        if (payload.marker === "base") {
            // 5-point star polygon. Drawn larger than the other
            // markers and with an outline so the base scenario stays
            // visible even when many sensitivity dots cluster on top.
            const r = 11;
            const points: string[] = [];
            for (let i = 0; i < 10; i++) {
                const radius = i % 2 === 0 ? r : r / 2;
                const angle = (Math.PI / 5) * i - Math.PI / 2;
                points.push(
                    `${cx + Math.cos(angle) * radius},${
                        cy + Math.sin(angle) * radius
                    }`
                );
            }
            return (
                <polygon
                    points={points.join(" ")}
                    fill={color}
                    stroke="#fff"
                    strokeWidth={1.5}
                />
            );
        }
        if (payload.marker === "self") {
            // Cross / X marker.
            const s = 5;
            return (
                <g stroke={color} strokeWidth={2}>
                    <line x1={cx - s} y1={cy - s} x2={cx + s} y2={cy + s} />
                    <line x1={cx - s} y1={cy + s} x2={cx + s} y2={cy - s} />
                </g>
            );
        }
        return <circle cx={cx} cy={cy} r={4} fill={color} />;
    };

    return (
        <Card className={css.chartCard}>
            <div className={css.chartHeader}>
                <H4 className={css.chartTitle}>Cost by dropoff</H4>
                <div className={css.chartHeaderActions}>
                    <SegmentedControl
                        options={[
                            {
                                label: "Number of projects",
                                value: "SCENARIO_COUNT"
                            },
                            {
                                label: "Percentage of total MW",
                                value: "MW_PERCENT"
                            }
                        ]}
                        value={dropoffX}
                        onValueChange={(v) => {
                            segmentTrackChangeDropoffChartXAxis({ xAxis: v });
                            setDropoffX(v as DropoffXAxis);
                        }}
                        small
                    />
                </div>
            </div>
            <ResponsiveContainer width="100%" height={320}>
                <ScatterChart
                    margin={{ top: 10, right: 20, bottom: 40, left: 40 }}
                >
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                    <XAxis
                        type="number"
                        dataKey="x"
                        domain={xAxisScale.domain}
                        ticks={xAxisScale.ticks}
                        tick={{ fontSize: 11 }}
                        tickFormatter={xTickFormatter}
                        label={{
                            value: xLabel,
                            position: "insideBottom",
                            offset: -10,
                            style: { fontSize: 11 }
                        }}
                    />
                    <YAxis
                        type="number"
                        dataKey="costValue"
                        domain={
                            metric === "RANK"
                                ? rankAxisScale.domain
                                : ["auto", "auto"]
                        }
                        ticks={
                            metric === "RANK" ? rankAxisScale.ticks : undefined
                        }
                        width={80}
                        tick={{ fontSize: 11 }}
                        tickFormatter={yFormatter}
                        label={{
                            value: yLabel,
                            angle: -90,
                            position: "left",
                            offset: 0,
                            style: { fontSize: 11, textAnchor: "middle" }
                        }}
                    />
                    <Tooltip
                        cursor={{ strokeDasharray: "3 3" }}
                        isAnimationActive={false}
                        content={({ payload }) => {
                            const d = payload?.[0]?.payload as Row | undefined;
                            if (!d) return null;
                            const yShown = yFormatter(d.costValue);
                            return (
                                <div className={css.tooltip}>
                                    <div>
                                        <b>{d.label}</b>
                                        {d.marker === "base" && " (base)"}
                                    </div>
                                    <div>{yShown}</div>
                                    {metric !== "RANK" &&
                                        d.rank !== undefined &&
                                        d.totalProjects !== undefined && (
                                            <div className={css.tooltipSubtle}>
                                                Rank by $/MW: P
                                                {d.rankPercentile} ({d.rank} of{" "}
                                                {d.totalProjects})
                                            </div>
                                        )}
                                    <div className={css.tooltipDivider} />
                                    <div className={css.tooltipSubtle}>
                                        Sensitivities
                                    </div>
                                    <div>
                                        Own downsize:{" "}
                                        {formatOwnDownsize(
                                            d.ownDownsizePct,
                                            d.ownNrisDownsizePct
                                        )}
                                    </div>
                                    <div>
                                        Other projects dropped:{" "}
                                        {d.otherProjectCount === 0
                                            ? "none"
                                            : `${d.otherProjectCount} project${
                                                  d.otherProjectCount === 1
                                                      ? ""
                                                      : "s"
                                              } · ${d.otherDropoffMw.toFixed(
                                                  0
                                              )} MW (${formatPercent(
                                                  d.otherDropoffPct
                                              )} of cluster)`}
                                    </div>
                                    <div>
                                        Cost assumption: {d.costAssumption}
                                    </div>
                                </div>
                            );
                        }}
                    />
                    <Scatter
                        data={rows}
                        shape={renderShape}
                        isAnimationActive={false}
                    />
                </ScatterChart>
            </ResponsiveContainer>
            <div className={css.chartLegend}>
                <span className={css.chartLegendItem}>
                    <span
                        className={css.chartLegendShape}
                        style={{ color: MARKER_COLOR.base }}
                    >
                        &#9733;
                    </span>
                    <span>Base Scenario</span>
                </span>
                <span className={css.chartLegendItem}>
                    <span
                        className={css.chartLegendDot}
                        style={{ background: MARKER_COLOR.sensitivity }}
                    />
                    <span>Current Project Unchanged</span>
                </span>
                <span className={css.chartLegendItem}>
                    <span
                        className={css.chartLegendShape}
                        style={{ color: MARKER_COLOR.self }}
                    >
                        &#10005;
                    </span>
                    <span>Current Project Modified</span>
                </span>
            </div>
        </Card>
    );
};
